/**
 * @file     BlogPostDetail.tsx
 * @purpose  The article on the reading template: category link, H1, the kit
 *           author note for posts Ray wrote, a hairline byline strip (date,
 *           read time, reviewer), cover image, then a 680px reading column
 *           beside a sticky "On this page" rail on wide screens. The rail is
 *           built from heading blocks, or from the h2s of a hand-written post's
 *           HTML, and only renders when there are at least two.
 */
'use client';

import { useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { resolveArticleAttribution } from '@/core/config/brand';
import { hubLayoutTokens, layoutTokens } from '@/design-system/tokens/layout';
import { hubTypographyTokens } from '@/design-system/tokens/typography';
import { zLayers } from '@/design-system/tokens/zLayers';
import { AuthorNote } from '@/features/seo/AuthorNote';
import { focusRingSx, hairline, headingFont, hubColors, hubRadius, reducedMotionSx, transition } from '@/components/hub/hubKitStyles';
import { ContentBlocks, collectHeadings } from '../blocks/ContentBlocks';
import { TOC_RAIL_WIDTH, shouldShowToc } from './articleGrid';
import { isVisiblyUpdated, splitNameTemplate } from './byline';
import { anchorHtmlHeadings } from './htmlHeadings';
import type { LocalBlogPost } from '../types/LocalBlogPost';

const CATEGORY_I18N: Record<LocalBlogPost['category'], { key: string; defaultValue: string }> = {
  'market-update': { key: 'blog.card.category.marketUpdate', defaultValue: 'Market Update' },
  'buyer-guide': { key: 'blog.card.category.buyerGuide', defaultValue: 'Buyer Guide' },
  'seller-guide': { key: 'blog.card.category.sellerGuide', defaultValue: 'Seller Guide' },
  presale: { key: 'blog.card.category.presale', defaultValue: 'Presale' },
  neighbourhood: { key: 'blog.card.category.neighbourhood', defaultValue: 'Neighbourhood' },
};

const formatDate = (iso: string, locale: string) =>
  new Date(iso).toLocaleDateString(locale || 'en-CA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });

type BlogPostDetailProps = {
  post: LocalBlogPost;
  bylineTemplate: string;
  /** Rendered at the end of the reading column (the topic CTA). */
  footer?: React.ReactNode;
};

const metaText = { m: 0, fontSize: 14, lineHeight: 1.5, color: hubColors.textMuted } as const;
const personLinkStyle = { color: hubColors.textPrimary, fontWeight: 500, textDecoration: 'none' } as const;

const READING_BODY_SX = {
  color: hubColors.textSecondary,
  fontSize: { xs: 17, md: 18 },
  lineHeight: 1.7,
  '& > :first-child': { mt: 0 },
  '& h2': {
    fontFamily: headingFont,
    fontSize: { xs: 22, md: 26 },
    fontWeight: 600,
    letterSpacing: '-0.015em',
    lineHeight: 1.2,
    color: hubColors.textPrimary,
    mt: '40px',
    mb: '12px',
    scrollMarginTop: 24,
  },
  '& h3': {
    fontFamily: headingFont,
    fontSize: { xs: 19, md: 20 },
    fontWeight: 600,
    color: hubColors.textPrimary,
    mt: '28px',
    mb: '8px',
  },
  '& p': { m: 0, mb: '18px', textWrap: 'pretty' },
  '& ul, & ol': { pl: '24px', mb: '18px' },
  '& li': { mb: '8px' },
  '& a': { color: hubColors.link, textUnderlineOffset: '3px' },
  '& a:hover': { color: hubColors.linkHover },
  '& strong': { color: hubColors.textPrimary },
  '& img': { maxWidth: '100%', height: 'auto', borderRadius: `${hubRadius.panel}px` },
  '& blockquote': { m: 0, my: '24px', pl: '18px', borderLeft: `3px solid ${hubColors.interactivePrimary}`, color: hubColors.textPrimary },

  // Engine block markup. Every rule below targets a class emitted by
  // services/content-engine/render.ts.
  '& figure': { m: 0, my: '28px' },
  '& figcaption': { fontSize: 14, fontWeight: 600, color: hubColors.textPrimary, mb: '12px' },
  '& .rc-stats dl': {
    display: 'grid',
    gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
    gap: '1px',
    m: 0,
    backgroundColor: hubColors.borderSubtle,
    border: hairline,
    borderRadius: `${hubRadius.panel}px`,
    overflow: 'hidden',
  },
  '& .rc-stats dl > div': { backgroundColor: hubColors.surfaceCard, p: '14px 16px' },
  '& .rc-stats dt': { fontSize: 12, color: hubColors.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em', mb: '4px' },
  '& .rc-stats dd': { m: 0, fontFamily: headingFont, fontSize: 22, fontWeight: 600, color: hubColors.textPrimary },
  '& .rc-stats dl > div p': { fontSize: 12, color: hubColors.textMuted, m: '4px 0 0', lineHeight: 1.4 },
  '& .rc-table': { overflowX: 'auto' },
  '& table': { width: '100%', borderCollapse: 'collapse', fontSize: 15 },
  '& th, & td': { textAlign: 'left', p: '10px 12px', borderBottom: hairline, color: hubColors.textSecondary },
  '& thead th': { color: hubColors.textMuted, fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em' },
  '& tbody th': { color: hubColors.textPrimary, fontWeight: 500 },
  '& .rc-listings ul, & .rc-roundup ul, & .rc-related ul': {
    listStyle: 'none',
    p: 0,
    m: 0,
    display: 'grid',
    gap: '1px',
    backgroundColor: hubColors.borderSubtle,
    border: hairline,
    borderRadius: `${hubRadius.panel}px`,
    overflow: 'hidden',
  },
  '& .rc-listings li, & .rc-roundup li, & .rc-related li': {
    backgroundColor: hubColors.surfaceCard,
    p: '12px 16px',
    m: 0,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    gap: '4px 14px',
    fontSize: 15,
    transition: transition('background-color'),
    '&:hover': { backgroundColor: hubColors.surfaceElevated },
  },
  '& .rc-listings li a, & .rc-roundup li a, & .rc-related li a': { color: hubColors.textPrimary, textDecoration: 'none', flex: '1 1 60%' },
  '& .rc-listings li span, & .rc-roundup li span': { color: hubColors.textMuted },
  '& .rc-listings-caption': { fontSize: 14, fontWeight: 600, color: hubColors.textPrimary, mb: '12px' },
  '& .rc-listings, & .rc-roundup, & .rc-related, & .rc-faq': { my: '28px' },
  '& .rc-disclosure': { fontSize: 12.5, color: hubColors.textMuted, borderTop: hairline, pt: '16px', mt: '32px' },
} as const;

function Dot() {
  return <Box component="span" aria-hidden="true" sx={{ width: 3, height: 3, borderRadius: `${layoutTokens.radius.pill}px`, backgroundColor: hubColors.textMuted, opacity: 0.6 }} />;
}

export const BlogPostDetail = ({ post, bylineTemplate, footer }: BlogPostDetailProps) => {
  const { t, i18n } = useTranslation();

  const blocks = post.blocks && post.blocks.length > 0 ? post.blocks : null;
  const anchored = useMemo(() => (blocks ? null : anchorHtmlHeadings(post.content)), [blocks, post.content]);
  const headings = useMemo(() => (blocks ? collectHeadings(blocks) : anchored?.headings ?? []), [blocks, anchored]);
  const attribution = resolveArticleAttribution(post);
  const [bylineBefore, bylineAfter] = splitNameTemplate(bylineTemplate);
  const showUpdated = isVisiblyUpdated(post.publishedAt, post.updatedAt);
  const showToc = shouldShowToc(headings.length);
  const category = CATEGORY_I18N[post.category];

  const [progress, setProgress] = useState(0);
  const [activeHeading, setActiveHeading] = useState<string | null>(null);

  useEffect(() => {
    const onScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(scrollable > 0 ? Math.min(1, Math.max(0, window.scrollY / scrollable)) : 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  useEffect(() => {
    if (headings.length === 0 || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActiveHeading(visible.target.id);
      },
      { rootMargin: '-96px 0px -70% 0px', threshold: 0 },
    );
    headings.forEach((heading) => {
      const el = document.getElementById(heading.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [headings]);

  const h1 = hubTypographyTokens.h1;

  return (
    <>
      {/* Reading progress */}
      <Box aria-hidden sx={{ position: 'fixed', top: 0, left: 0, right: 0, height: 2, zIndex: zLayers.stickyBar, pointerEvents: 'none' }}>
        <Box
          sx={{
            height: '100%',
            width: `${(progress * 100).toFixed(2)}%`,
            backgroundColor: hubColors.interactivePrimary,
            transition: 'width 120ms linear',
            ...reducedMotionSx,
          }}
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          alignItems: 'start',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            lg: showToc ? `minmax(0, ${hubLayoutTokens.readingColumn}px) ${TOC_RAIL_WIDTH}` : `minmax(0, ${hubLayoutTokens.readingColumn}px)`,
          },
          justifyContent: 'start',
          columnGap: { lg: 7 },
        }}
      >
        <Box component="article" sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: { xs: 3, md: 4 } }}>
          <Box component="header" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              component={Link}
              href={`/blog?category=${post.category}`}
              sx={{
                alignSelf: 'flex-start',
                display: 'inline-flex',
                alignItems: 'center',
                minHeight: 32,
                fontSize: 13,
                fontWeight: 600,
                color: hubColors.accentText,
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
                ...focusRingSx,
              }}
            >
              {t(category.key, { defaultValue: category.defaultValue })}
            </Box>

            <Box
              component="h1"
              sx={{
                m: 0,
                fontFamily: headingFont,
                fontSize: { xs: 34, md: 48, lg: 56 },
                fontWeight: h1.fontWeight,
                lineHeight: 1.06,
                letterSpacing: h1.letterSpacing,
                color: hubColors.textPrimary,
                textWrap: 'balance',
                overflowWrap: 'break-word',
              }}
            >
              {post.title}
            </Box>

            {attribution.author.kind === 'person' ? (
              <Box sx={{ fontSize: 15, lineHeight: 1.55, color: hubColors.textSecondary, maxWidth: '62ch' }}>
                <AuthorNote linkColor={hubColors.link} />
              </Box>
            ) : null}

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                columnGap: 2,
                rowGap: 1,
                py: 2,
                borderTop: hairline,
                borderBottom: hairline,
              }}
            >
              <Box component="p" data-testid="blog-byline" sx={{ ...metaText, color: hubColors.textSecondary }}>
                {bylineBefore}
                {attribution.author.kind === 'person' ? (
                  <Link href="/about-ray" style={personLinkStyle}>{attribution.author.name}</Link>
                ) : (
                  attribution.author.name
                )}
                {bylineAfter}
              </Box>
              {attribution.reviewedBy ? (
                <>
                  <Dot />
                  <Box component="p" data-testid="blog-reviewed-by" sx={{ ...metaText, color: hubColors.textSecondary }}>
                    {t('blog.post.reviewedBy', { defaultValue: 'Reviewed by' })}{' '}
                    <Link href="/about-ray" style={personLinkStyle}>{attribution.reviewedBy.name}</Link>
                  </Box>
                </>
              ) : null}
              <Dot />
              <Box component="p" sx={metaText}>
                <time dateTime={post.publishedAt}>{formatDate(post.publishedAt, i18n.language)}</time>
              </Box>
              {showUpdated && post.updatedAt ? (
                <>
                  <Dot />
                  <Box component="p" data-testid="blog-updated" sx={metaText}>
                    {t('blog.post.updated', { defaultValue: 'Updated {{date}}', date: formatDate(post.updatedAt, i18n.language) })}
                  </Box>
                </>
              ) : null}
              <Dot />
              <Box component="p" sx={metaText}>
                {t('blog.card.minRead', { defaultValue: '{{count}} min read', count: post.readTimeMinutes })}
              </Box>
            </Box>
          </Box>

          {post.coverImage ? (
            <Box sx={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', borderRadius: `${hubRadius.panel}px`, overflow: 'hidden', backgroundColor: hubColors.skeleton }}>
              <Image
                src={post.coverImage}
                alt={t('blog.coverImageAlt', { defaultValue: 'Cover image for {{title}}', title: post.title })}
                fill
                quality={85}
                // The hero is the LCP element on every article. Without priority it
                // lazy-loads below the fold heuristic and pushes LCP past target.
                priority
                sizes="(max-width: 899px) 100vw, 680px"
                style={{ objectFit: 'cover' }}
              />
            </Box>
          ) : null}

          {/* Article body: structured blocks when the engine produced them, the
              stored body HTML (with anchored h2s) for hand-written posts. */}
          {blocks ? (
            <ContentBlocks blocks={blocks} />
          ) : (
            <Box dangerouslySetInnerHTML={{ __html: anchored?.html ?? post.content }} sx={READING_BODY_SX} />
          )}

          {post.tags && post.tags.length > 0 ? (
            <Box component="ul" aria-label={t('blog.post.tags', { defaultValue: 'Topics' })} sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {post.tags.map((tag) => (
                <Box
                  component="li"
                  key={tag}
                  sx={{ px: 1.25, py: 0.5, borderRadius: `${layoutTokens.radius.pill}px`, border: hairline, fontSize: 13, color: hubColors.textMuted }}
                >
                  {tag}
                </Box>
              ))}
            </Box>
          ) : null}

          {footer}
        </Box>

        {showToc ? (
          <Box
            component="nav"
            aria-label={t('blog.toc.label', { defaultValue: 'On this page' })}
            sx={{
              display: { xs: 'none', lg: 'flex' },
              flexDirection: 'column',
              position: 'sticky',
              top: 'calc(var(--chrome-inset, 0px) + 32px)',
              maxHeight: 'calc(100vh - var(--nav-height, 0px) - 64px)',
              overflowY: 'auto',
            }}
          >
            <Box component="p" sx={{ m: 0, mb: 1, fontSize: 13, fontWeight: 500, color: hubColors.textMuted }}>
              {t('blog.toc.label', { defaultValue: 'On this page' })}
            </Box>
            {headings.map((heading) => {
              const active = activeHeading === heading.id;
              return (
                <Box
                  key={heading.id}
                  component="a"
                  href={`#${heading.id}`}
                  data-focus="target"
                  aria-current={active ? 'location' : undefined}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: 44,
                    py: 1,
                    pl: heading.level === 3 ? 3 : 1.5,
                    fontSize: 14.5,
                    lineHeight: 1.35,
                    textDecoration: 'none',
                    borderLeft: `2px solid ${active ? hubColors.interactivePrimary : hubColors.borderSubtle}`,
                    color: active ? hubColors.textPrimary : hubColors.textSecondary,
                    transition: transition('color', 'border-color'),
                    '&:hover': { color: hubColors.textPrimary },
                    ...focusRingSx,
                    ...reducedMotionSx,
                  }}
                >
                  {heading.text}
                </Box>
              );
            })}
          </Box>
        ) : null}
      </Box>
    </>
  );
};
